import React, { useEffect, useMemo, useState } from 'react';

import { useNotification } from '@hero/store/hooks';

import { mask, string } from './types';
import { getComponentByType } from './utils';

export const getDefaultColumn = ({ components } = {}) => ({
  cell: ({ getValue, row, column, table, isRowEditing }) => {
    const { showError } = useNotification();
    const { editMode } = table.options;

    const initialValue = getValue();

    const [value, setValue] = useState(initialValue);
    const [isFocused, setIsFocused] = useState(false);

    const onFocus = () => {
      if (editMode === 'inline') {
        setIsFocused(true);
      }
    };

    const onBlur = () => {
      if (editMode === 'inline') {
        setIsFocused(false);
        if (
          column.columnDef?.type === mask &&
          column.columnDef?.componentProps?.errorText &&
          column.columnDef?.componentProps?.isValid &&
          !column.columnDef?.componentProps?.isValid(value)
        ) {
          showError({
            type: 'toast',
            message: column.columnDef?.componentProps?.errorText
          });
          setValue(initialValue);
          return;
        }

        table.options.meta?.updateData(
          row.original,
          column.id,
          column.columnDef?.componentProps?.formatSave?.(value) ?? value
        );
      }
    };

    const onCancel = () => {
      if (editMode === 'inline') {
        setIsFocused(false);
        setValue(initialValue);
      }
    };

    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    const { type = string, isValidFn, editRow, ...columnProps } = column?.columnDef || {};
    const { Component, props } = getComponentByType({ components, type });

    const hasError = useMemo(() => {
      return isValidFn ? !isValidFn?.(value) : false;
    }, [value]);

    const isEditable =
      (column?.columnDef?.editable &&
        (editMode === 'inline' || (editMode === 'row' && isRowEditing))) ||
      editRow === row.id;

    const isActive =
      (isEditable &&
        ((editMode === 'inline' && isFocused) || (editMode === 'row' && isRowEditing))) ||
      editRow === row.id;

    return (
      <Component
        isSelected={row.getIsSelected()}
        readOnly={!isEditable}
        value={value}
        onChange={(e, val) => {
          setValue(val || e?.target?.value);
        }}
        onCancel={onCancel}
        onBlur={onBlur}
        onFocus={onFocus}
        isFocused={isActive}
        error={hasError}
        columnProps={columnProps}
        {...props}
      />
    );
  }
});
